import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { Variable } from '@kentik/ui-shared/boards/boards';
import { Flex, Text } from 'core/components';
import { getGuidedDimensionOptions } from '@kentik/ui-shared/query/measurements/guidedDimensions';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import DimensionTreeSelector from './panels/ude/filters/DimensionTreeSelector';
import { UdeFormProps } from './panels/ude/form.types';
import ToggleFilterButton from './panels/ude/filters/ToggleFilterButton';

interface Props {
  form: UdeFormProps['form'];
  measurements: Measurement[];
  panel: BoardPanelModel;
  variable: Variable;
}

const VariableRow = styled.div`
  display: flex;
  align-items: flex-start;
  white-space: nowrap;

  .toggle {
    visibility: hidden;

    &.toggle-off {
      visibility: visible;
    }
  }

  .${Classes.POPOVER_WRAPPER} {
    margin-inline: -4px;
  }

  &.${Classes.DISABLED} {
    .content > * {
      opacity: 0.35;

      &:is(.${Classes.POPOVER_WRAPPER}) {
        transition: opacity 0.2s;

        &:hover,
        &:has(.${Classes.POPOVER_OPEN}) {
          opacity: 1;
        }
      }
    }
  }

  &:hover {
    .toggle {
      visibility: visible;
    }
  }
`;

function PanelVariableMapping({ form, measurements, panel, variable }: Props) {
  const measurementName = form.getValue('config.data.measurement');
  const variableMapping = panel.getVariableMapping(variable.name);

  const handleToggle = useCallback(() => {
    panel.toggleVariableMapping(variable.name);
    form.setValuesDeep({ config: { variableMappings: panel.get('config').variableMappings } });
    form.onChange({ form, formValues: form.getValues() });
  }, [form, panel, variable]);

  const handleMappingChange = useCallback(
    (filterField: string) => {
      panel.setVariableMapping({ variableName: variable.name, filterField });
      form.setValuesDeep({ config: { variableMappings: panel.get('config').variableMappings } });
      form.onChange({ form, formValues: form.getValues() });
    },
    [form, panel, variable]
  );

  if (!measurementName) {
    return null;
  }

  let allowedValues: string[] | undefined;
  if (variable.type === 'guided') {
    allowedValues = getGuidedDimensionOptions(variable.name, measurementName, measurements);
  }

  return (
    <VariableRow className={classNames({ [Classes.DISABLED]: variableMapping?.disabled })}>
      <Flex className="content" alignItems="baseline" gap="4px" maxWidth="calc(100% - 24px)" overflow="hidden">
        <Text fontWeight={600} small>
          {variable.name}
        </Text>
        <Text small muted>
          maps to
        </Text>
        <DimensionTreeSelector
          value={variableMapping?.filterField ?? ''}
          onChange={handleMappingChange}
          measurement={measurementName}
          measurements={measurements}
          allowedValues={allowedValues}
          minimal
        />
      </Flex>
      <ToggleFilterButton
        on={variableMapping ? !variableMapping.disabled : false}
        disabled={!variableMapping}
        onToggle={handleToggle}
      />
    </VariableRow>
  );
}

export default observer(PanelVariableMapping);
